import { useState } from "react";
import { Plus } from "lucide-react";
import { pickBucketInput, useBuckets, useSaveBucket, type Bucket } from "@/api/finance";
import { BucketForm } from "@/components/forms/BucketForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

type Editing = { mode: "new" } | { mode: "edit"; bucket: Bucket } | null;

export function BucketsPanel() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const buckets = useBuckets(includeArchived);
  const save = useSaveBucket();

  const active = (buckets.data ?? []).filter((bucket) => !bucket.archived);
  const total =
    active.reduce((sum, bucket) => sum + Math.round(Number(bucket.default_percent) * 100), 0) / 100;
  const nextSortOrder = Math.max(0, ...(buckets.data ?? []).map((b) => b.sort_order)) + 1;

  return (
    <div className="flex flex-col gap-4">
      <Alert title="How every invoice is split">
        Each issued invoice is divided into these buckets by their default share, and can be
        changed for one invoice on its page. Changing a default here affects invoices issued from
        now on; earlier invoices keep the split they had.
      </Alert>
      {buckets.data && total !== 100 && (
        <Alert variant="warning" title={`The defaults add up to ${total}%, not 100%`}>
          Adjust the shares so new invoices are split in full.
        </Alert>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Checkbox
          id="buckets-archived"
          label="Show archived buckets"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add bucket
          </Button>
        )}
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New bucket" : `Edit ${editing.bucket.name}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BucketForm
              key={editing.mode === "edit" ? `${editing.bucket.id}-${editing.bucket.version}` : "new"}
              initial={editing.mode === "edit" ? editing.bucket : null}
              nextSortOrder={nextSortOrder}
              submitLabel={editing.mode === "new" ? "Add bucket" : "Save bucket"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.bucket.id, version: editing.bucket.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void buckets.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      <RequestError error={save.error} onReload={() => void buckets.refetch()} />

      {buckets.isPending ? (
        <p className="text-muted-foreground">Loading buckets…</p>
      ) : buckets.isError ? (
        <RequestError error={buckets.error} />
      ) : (
        <div className="table-frame">
          <table className="data-table">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Bucket</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 text-right font-medium">Default share</th>
                <th className="px-4 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {buckets.data.map((bucket) => (
                <tr key={bucket.id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <span className="font-medium">{bucket.name}</span>
                    {bucket.archived && (
                      <Badge variant="muted" className="ml-2">
                        Archived
                      </Badge>
                    )}
                    <span className="block text-xs text-muted-foreground">
                      {[bucket.default_payee, bucket.purpose].filter(Boolean).join(" · ")}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={bucket.kind === "payout" ? "warning" : "secondary"}>
                      {bucket.kind === "payout" ? "Payout" : "Pool"}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {Number(bucket.default_percent)}%
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {!bucket.archived && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ mode: "edit", bucket })}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={save.isPending}
                      onClick={() =>
                        save.mutate({
                          input: { ...pickBucketInput(bucket), archived: !bucket.archived },
                          existing: { id: bucket.id, version: bucket.version },
                        })
                      }
                    >
                      {bucket.archived ? "Restore" : "Archive"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
